import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { DataContext } from '../../context/DataProvider';
import Button from '../../components/common/Button';
import { Plus, Trash2, LogOut, Edit2, X, Upload, Users, BookOpen, Eye, CheckCircle, XCircle } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { supabase } from '../../supabase';
import CustomDialog from '../../components/common/CustomDialog';

const AdminDashboard = () => {
    const { 
        courses, addCourse, deleteCourse, updateCourse, 
        jobs, addJob, deleteJob, updateJob, 
        teamMembers, addTeamMember, deleteTeamMember, updateTeamMember,
        projects, addProject, deleteProject, updateProject,
        blogs, addBlog, deleteBlog, updateBlog,
        loading 
    } = useContext(DataContext);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('courses');
    const [editingId, setEditingId] = useState(null);

    // Auth Check
    useEffect(() => {
        if (!localStorage.getItem('isAdmin')) {
            navigate('/admin');
        }
    }, [navigate]);

    // Handle Back Button & Logout Logic
    useEffect(() => {
        // Push a state to trap the back button
        window.history.pushState(null, null, window.location.pathname);

        const handlePopState = (event) => {
            // Prevent default back behavior initially
            const confirmLogout = window.confirm("Do you want to logout from admin panel?");

            if (confirmLogout) {
                localStorage.removeItem('isAdmin');
                // Allow the back navigation to proceed (which effectively goes back from the pushed state)
                // But since we are logged out, we want to go back to the page BEFORE admin dashboard
                navigate(-1); // Go back twice: once for the pushed state, once to exit dashboard
            } else {
                // Push state again to re-trap
                window.history.pushState(null, null, window.location.pathname);
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate]);

    const handleLogout = () => {
        if (window.confirm("Are you sure you want to logout?")) {
            localStorage.removeItem('isAdmin');
            navigate('/admin');
        }
    };

    // Form States
    const [courseForm, setCourseForm] = useState({ title: '', desc: '', level: 'Beginner', format: 'Video Course', price: '', status: 'Enroll Now', thumbnail_url: '', batch_date: '' });

    // Debug render
    // console.log('Current Form State:', courseForm);

    const [jobForm, setJobForm] = useState({ title: '', type: 'Remote', desc: '' });
    const [teamForm, setTeamForm] = useState({ name: '', role: '', image_url: '' });
    const [projectForm, setProjectForm] = useState({ title: '', category: '', description: '', image_url: '', project_link: '' });
    const [blogForm, setBlogForm] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: 'UI/UX',
        author: 'PyrusMedia Team',
        read_time: '5 min read',
        cover_image: '',
        is_published: true
    });
    const [uploading, setUploading] = useState(false);

    // Helper to generate clean slug
    const createSlug = (text) => {
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]+/g, '')
            .replace(/--+/g, '-');
    };

    // Custom Dialog State
    const [dialog, setDialog] = useState({ isOpen: false, title: '', message: '', type: 'success' });

    const resetForms = () => {
        setEditingId(null);
        setCourseForm({ title: '', desc: '', level: 'Beginner', format: 'Video Course', price: '', status: 'Enroll Now', thumbnail_url: '', batch_date: '' });
        setJobForm({ title: '', type: 'Remote', desc: '' });
        setTeamForm({ name: '', role: '', image_url: '' });
        setProjectForm({ title: '', category: '', description: '', image_url: '', project_link: '' });
        setBlogForm({
            title: '',
            slug: '',
            excerpt: '',
            content: '',
            category: 'UI/UX',
            author: 'PyrusMedia Team',
            read_time: '5 min read',
            cover_image: '',
            is_published: true
        });
    };

    const [pendingDelete, setPendingDelete] = useState({ type: '', id: null });

    const closeDialog = () => {
        setDialog({ ...dialog, isOpen: false });
        setPendingDelete({ type: '', id: null });
    };

    const handleConfirmAction = async () => {
        if (!pendingDelete.id) return;

        closeDialog(); // Close confirmation dialog

        let result;
        if (pendingDelete.type === 'course') {
            result = await deleteCourse(pendingDelete.id);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Course Deleted', message: 'The course has been successfully removed.', type: 'success' });
            }
        } else if (pendingDelete.type === 'job') {
            result = await deleteJob(pendingDelete.id);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Job Deleted', message: 'The job position has been successfully removed.', type: 'success' });
            }
        } else if (pendingDelete.type === 'team') {
            result = await deleteTeamMember(pendingDelete.id);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Team Member Removed', message: 'The team member has been successfully removed.', type: 'success' });
            }
        } else if (pendingDelete.type === 'project') {
            result = await deleteProject(pendingDelete.id);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Project Deleted', message: 'The project has been successfully removed.', type: 'success' });
            }
        } else if (pendingDelete.type === 'blog') {
            result = await deleteBlog(pendingDelete.id);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Blog Article Deleted', message: 'The article has been successfully removed.', type: 'success' });
            }
        }

        if (result && !result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        }
    };

    const handleEditCourse = (course) => {
        console.log('Editing course - Incoming Data:', course);
        setEditingId(course.id);
        const newFormState = {
            id: course.id,
            title: course.title || '',
            desc: course.desc || '',
            level: course.level || 'Beginner',
            format: course.format || 'Video Course',
            price: course.price || '',
            status: course.status || 'Enroll Now',
            thumbnail_url: course.thumbnail_url || '',
            batch_date: course.batch_date ? new Date(course.batch_date).toISOString().split('T')[0] : ''
        };
        // Use functional update to ensure we don't depend on stale closures, 
        // essentially replacing user action with this state.
        setCourseForm(newFormState);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleEditJob = (job) => {
        setEditingId(job.id);
        setJobForm(job);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleEditTeamMember = (member) => {
        setEditingId(member.id);
        setTeamForm(member);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleImageUpload = async (e, type = 'course') => {
        try {
            const file = e.target.files[0];
            if (!file) return;

            setUploading(true);
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;
            const targetBucket = type === 'blog' ? 'blog-assets' : 'course-assets';

            let finalBucket = targetBucket;

            // Try uploading to targetBucket first
            const { error: err } = await supabase.storage
                .from(targetBucket)
                .upload(filePath, file);

            if (err) {
                // If blog-assets bucket is not yet created in Supabase console, fallback to course-assets
                console.warn(`Upload to ${targetBucket} failed, falling back to course-assets:`, err.message);
                const { error: fallbackErr } = await supabase.storage
                    .from('course-assets')
                    .upload(filePath, file);

                if (fallbackErr) {
                    throw fallbackErr;
                }
                finalBucket = 'course-assets';
            }

            const { data } = supabase.storage
                .from(finalBucket)
                .getPublicUrl(filePath);

            if (type === 'team') {
                setTeamForm(prev => ({ ...prev, image_url: data.publicUrl }));
            } else if (type === 'project') {
                setProjectForm(prev => ({ ...prev, image_url: data.publicUrl }));
            } else if (type === 'blog') {
                setBlogForm(prev => ({ ...prev, cover_image: data.publicUrl }));
            } else {
                setCourseForm(prev => ({ ...prev, thumbnail_url: data.publicUrl }));
            }
        } catch (error) {
            setDialog({ isOpen: true, title: 'Upload Failed', message: error.message, type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleSubmitCourse = async (e) => {
        e.preventDefault();
        let result;
        if (editingId) {
            result = await updateCourse(courseForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Course Updated', message: 'The course has been successfully updated.', type: 'success' });
            }
        } else {
            result = await addCourse(courseForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Course Added', message: 'New course has been successfully added.', type: 'success' });
            }
        }

        if (!result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        } else {
            resetForms();
        }
    };

    const handleSubmitJob = async (e) => {
        e.preventDefault();
        let result;
        if (editingId) {
            result = await updateJob(jobForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Job Updated', message: 'The job position has been successfully updated.', type: 'success' });
            }
        } else {
            result = await addJob({ ...jobForm, role: jobForm.title });
            if (result.success) {
                setDialog({ isOpen: true, title: 'Job Added', message: 'New job position has been successfully added.', type: 'success' });
            }
        }

        if (!result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        } else {
            resetForms();
        }
    };

    const handleSubmitTeam = async (e) => {
        e.preventDefault();
        let result;
        if (editingId) {
            result = await updateTeamMember(teamForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Team Member Updated', message: 'Team member details updated successfully.', type: 'success' });
            }
        } else {
            result = await addTeamMember(teamForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Team Member Added', message: 'New team member added successfully.', type: 'success' });
            }
        }

        if (!result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        } else {
            resetForms();
        }
    };

    const handleDeleteCourse = (id) => {
        setPendingDelete({ type: 'course', id });
        setDialog({
            isOpen: true,
            title: 'Delete Course',
            message: 'Are you sure you want to delete this course? This action cannot be undone.',
            type: 'confirm'
        });
    };

    const handleDeleteJob = (id) => {
        setPendingDelete({ type: 'job', id });
        setDialog({
            isOpen: true,
            title: 'Delete Job',
            message: 'Are you sure you want to delete this job position? This action cannot be undone.',
            type: 'confirm'
        });
    };

    const handleDeleteTeamMember = (id) => {
        setPendingDelete({ type: 'team', id });
        setDialog({
            isOpen: true,
            title: 'Delete Team Member',
            message: 'Are you sure you want to delete this team member? This action cannot be undone.',
            type: 'confirm'
        });
    };

    const handleEditProject = (project) => {
        setEditingId(project.id);
        setProjectForm(project);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmitProject = async (e) => {
        e.preventDefault();
        let result;
        if (editingId) {
            result = await updateProject(projectForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Project Updated', message: 'The project has been successfully updated.', type: 'success' });
            }
        } else {
            result = await addProject(projectForm);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Project Added', message: 'New project added successfully.', type: 'success' });
            }
        }

        if (!result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        } else {
            resetForms();
        }
    };

    const handleDeleteProject = (id) => {
        setPendingDelete({ type: 'project', id });
        setDialog({
            isOpen: true,
            title: 'Delete Project',
            message: 'Are you sure you want to delete this project? This action cannot be undone.',
            type: 'confirm'
        });
    };

    const handleEditBlog = (blog) => {
        setEditingId(blog.id);
        setBlogForm({
            id: blog.id,
            title: blog.title || '',
            slug: blog.slug || '',
            excerpt: blog.excerpt || '',
            content: blog.content || '',
            category: blog.category || 'UI/UX',
            author: blog.author || 'PyrusMedia Team',
            read_time: blog.read_time || '5 min read',
            cover_image: blog.cover_image || '',
            is_published: blog.is_published !== false
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmitBlog = async (e) => {
        e.preventDefault();
        let result;
        // Ensure slug is clean
        const finalSlug = blogForm.slug ? createSlug(blogForm.slug) : createSlug(blogForm.title);
        const finalBlog = { ...blogForm, slug: finalSlug };

        if (editingId) {
            result = await updateBlog(finalBlog);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Article Updated', message: 'The blog article has been successfully updated.', type: 'success' });
            }
        } else {
            result = await addBlog(finalBlog);
            if (result.success) {
                setDialog({ isOpen: true, title: 'Article Published', message: 'New blog article has been successfully created.', type: 'success' });
            }
        }

        if (!result.success) {
            setDialog({ isOpen: true, title: 'Error', message: result.message, type: 'error' });
        } else {
            resetForms();
        }
    };

    const handleDeleteBlog = (id) => {
        setPendingDelete({ type: 'blog', id });
        setDialog({
            isOpen: true,
            title: 'Delete Blog Article',
            message: 'Are you sure you want to delete this article? This action cannot be undone.',
            type: 'confirm'
        });
    };

    return (
        <>
            <Helmet>
                <title>Admin Dashboard | PyrusMedia</title>
            </Helmet>
            <div className="min-h-screen bg-white pt-32 pb-20">
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center mb-12">
                        <h1 className="text-4xl font-bold text-gray-900">Admin Dashboard</h1>
                        <button onClick={handleLogout} className="flex items-center gap-2 text-red-500 hover:text-red-400">
                            <LogOut size={20} /> Logout
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-4 mb-8 border-b border-gray-200 pb-4">
                        <button
                            onClick={() => setActiveTab('courses')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${activeTab === 'courses' ? 'bg-[#bebd19] text-black' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            Manage Courses
                        </button>
                        <button
                            onClick={() => setActiveTab('jobs')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${activeTab === 'jobs' ? 'bg-[#bebd19] text-black' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            Manage Jobs
                        </button>
                        <button
                            onClick={() => setActiveTab('team')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${activeTab === 'team' ? 'bg-[#bebd19] text-black' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            Manage Team
                        </button>
                        <button
                            onClick={() => setActiveTab('projects')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${activeTab === 'projects' ? 'bg-[#bebd19] text-black' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            Manage Work
                        </button>
                        <button
                            onClick={() => setActiveTab('blogs')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${activeTab === 'blogs' ? 'bg-[#bebd19] text-black' : 'text-gray-500 hover:text-gray-900'}`}
                        >
                            Manage Blog
                        </button>
                    </div>

                    {activeTab === 'courses' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* List */}
                            <div className="lg:col-span-2 space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Existing Courses</h2>
                                {loading ? (
                                    <div className="text-gray-500 py-10 text-center">Loading courses...</div>
                                ) : courses.length > 0 ? (
                                    courses.map(course => (
                                        <div key={course.id} className="bg-gray-100 p-6 rounded-xl border border-gray-200 flex justify-between items-center group">
                                            <div className="flex items-center gap-4">
                                                {course.thumbnail_url && (
                                                    <img src={course.thumbnail_url} alt={course.title} className="w-16 h-16 object-cover rounded-md" />
                                                )}
                                                <div>
                                                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#bebd19] transition-colors">{course.title}</h3>
                                                    <p className="text-sm text-gray-400">{course.level} • <span>&#8377;</span> {course.price.replace(/[^0-9.]/g, '')}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleEditCourse(course)} className="text-blue-500 hover:bg-blue-500/10 p-2 rounded-lg transition-colors">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button onClick={() => handleDeleteCourse(course.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-10 bg-gray-100 rounded-xl border border-gray-200 border-dashed">
                                        <p className="text-gray-400">No courses added yet.</p>
                                    </div>
                                )}
                            </div>

                            {/* Add/Edit Form */}
                            <div className="bg-gray-100 p-6 rounded-xl border border-gray-200 h-fit sticky top-24">
                                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                                        {editingId ? 'Edit Course' : 'Add New Course'}
                                    </span>
                                    {editingId && (
                                        <button onClick={resetForms} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
                                            <X size={14} /> Cancel
                                        </button>
                                    )}
                                </h2>
                                <form onSubmit={handleSubmitCourse} className="space-y-3">
                                    <input required placeholder="Course Title" value={courseForm.title} onChange={e => setCourseForm(prev => ({ ...prev, title: e.target.value }))} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />

                                    {/* Image Upload */}
                                    <div className="space-y-2">
                                        <label className="block text-gray-500 text-sm">Thumbnail Image</label>
                                        <div className="flex gap-2 items-center">
                                            <label className="cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-900 px-3 py-2 rounded flex items-center gap-2 text-sm transition-colors">
                                                <Upload size={16} />
                                                {uploading ? 'Uploading...' : 'Choose Image'}
                                                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                                            </label>
                                            {courseForm.thumbnail_url && (
                                                <img src={courseForm.thumbnail_url} alt="Preview" className="h-10 w-10 object-cover rounded border border-gray-300" />
                                            )}
                                        </div>
                                    </div>

                                    <div className="bg-white text-black rounded">
                                        <ReactQuill
                                            theme="snow"
                                            value={courseForm.desc}
                                            onChange={value => setCourseForm(prev => ({ ...prev, desc: value }))}
                                            placeholder="Description"
                                            className="bg-white text-black rounded"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <input required placeholder="Level (e.g. Beginner)" value={courseForm.level} onChange={e => setCourseForm(prev => ({ ...prev, level: e.target.value }))} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />
                                        <input 
                                            required 
                                            placeholder="Price (e.g. 4999)" 
                                            value={courseForm.price} 
                                            onChange={e => {
                                                const val = e.target.value;
                                                // Allow numbers and one decimal point
                                                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                                    setCourseForm(prev => ({ ...prev, price: val }));
                                                }
                                            }}
                                            className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" 
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-500 text-xs mb-1">Batch Start Date</label>
                                        <input
                                            type="date"
                                            value={courseForm.batch_date}
                                            onChange={e => setCourseForm(prev => ({ ...prev, batch_date: e.target.value }))}
                                            className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none "
                                        />
                                    </div>
                                    <select value={courseForm.status} onChange={e => setCourseForm(prev => ({ ...prev, status: e.target.value }))} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none">
                                        <option value="Enroll Now">Enroll Now</option>
                                        <option value="Coming Soon">Coming Soon</option>
                                        <option value="Waitlist">Waitlist</option>
                                    </select>
                                    <Button type="submit" disabled={uploading} className={`w-full justify-center mt-2 ${editingId ? 'bg-blue-600 hover:bg-blue-700 text-gray-900' : ''}`}>
                                        {uploading ? 'Please wait...' : (editingId ? 'Update Course' : 'Add Course')}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    )}

                    {activeTab === 'jobs' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* List */}
                            <div className="lg:col-span-2 space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Open Positions</h2>
                                {loading ? (
                                    <div className="text-gray-500 py-10 text-center">Loading jobs...</div>
                                ) : jobs.length > 0 ? (
                                    jobs.map(job => (
                                        <div key={job.id} className="bg-gray-100 p-6 rounded-xl border border-gray-200 flex justify-between items-center group">
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#bebd19] transition-colors">{job.title}</h3>
                                                <p className="text-sm text-gray-400">{job.type}</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleEditJob(job)} className="text-blue-500 hover:bg-blue-500/10 p-2 rounded-lg transition-colors">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button onClick={() => handleDeleteJob(job.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-10 bg-gray-100 rounded-xl border border-gray-200 border-dashed">
                                        <p className="text-gray-400">No active job openings.</p>
                                    </div>
                                )}
                            </div>

                            {/* Add/Edit Form */}
                            <div className="bg-gray-100 p-6 rounded-xl border border-gray-200 h-fit sticky top-24">
                                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                                        {editingId ? 'Edit Job' : 'Add New Job'}
                                    </span>
                                    {editingId && (
                                        <button onClick={resetForms} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
                                            <X size={14} /> Cancel
                                        </button>
                                    )}
                                </h2>
                                <form onSubmit={handleSubmitJob} className="space-y-3">
                                    <input required placeholder="Job Title" value={jobForm.title} onChange={e => setJobForm({ ...jobForm, title: e.target.value })} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />
                                    <input required placeholder="Type (e.g. Remote / Full-time)" value={jobForm.type} onChange={e => setJobForm({ ...jobForm, type: e.target.value })} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />
                                    <textarea required rows="3" placeholder="Short Description" value={jobForm.desc} onChange={e => setJobForm({ ...jobForm, desc: e.target.value })} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />
                                    <Button type="submit" className={`w-full justify-center mt-2 ${editingId ? 'bg-blue-600 hover:bg-blue-700 text-gray-900' : ''}`}>
                                        {editingId ? 'Update Job' : 'Add Job'}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    )}

                    {activeTab === 'team' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* List */}
                            <div className="lg:col-span-2 space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Team Members</h2>
                                {loading ? (
                                    <div className="text-gray-500 py-10 text-center">Loading team...</div>
                                ) : teamMembers.length > 0 ? (
                                    teamMembers.map(member => (
                                        <div key={member.id} className="bg-gray-100 p-6 rounded-xl border border-gray-200 flex justify-between items-center group">
                                            <div className="flex items-center gap-4">
                                                {member.image_url ? (
                                                    <img src={member.image_url} alt={member.name} className="w-16 h-16 object-cover rounded-full border border-gray-200" />
                                                ) : (
                                                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">
                                                        <Users size={24} />
                                                    </div>
                                                )}
                                                <div>
                                                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#bebd19] transition-colors">{member.name}</h3>
                                                    <p className="text-sm text-gray-400">{member.role}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleEditTeamMember(member)} className="text-blue-500 hover:bg-blue-500/10 p-2 rounded-lg transition-colors">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button onClick={() => handleDeleteTeamMember(member.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-10 bg-gray-100 rounded-xl border border-gray-200 border-dashed">
                                        <p className="text-gray-400">No team members added yet.</p>
                                    </div>
                                )}
                            </div>

                            {/* Add/Edit Form */}
                            <div className="bg-gray-100 p-6 rounded-xl border border-gray-200 h-fit sticky top-24">
                                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                                        {editingId ? 'Edit Member' : 'Add Team Member'}
                                    </span>
                                    {editingId && (
                                        <button onClick={resetForms} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
                                            <X size={14} /> Cancel
                                        </button>
                                    )}
                                </h2>
                                <form onSubmit={handleSubmitTeam} className="space-y-3">
                                    <input required placeholder="Name" value={teamForm.name} onChange={e => setTeamForm({ ...teamForm, name: e.target.value })} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />
                                    <input required placeholder="Role (e.g. CEO, Developer)" value={teamForm.role} onChange={e => setTeamForm({ ...teamForm, role: e.target.value })} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />
                                    
                                    {/* Image Upload */}
                                    <div className="space-y-2">
                                        <label className="block text-gray-500 text-sm">Profile Photo</label>
                                        <div className="flex gap-2 items-center">
                                            <label className="cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-900 px-3 py-2 rounded flex items-center gap-2 text-sm transition-colors">
                                                <Upload size={16} />
                                                {uploading ? 'Uploading...' : 'Choose Image'}
                                                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'team')} className="hidden" disabled={uploading} />
                                            </label>
                                            
                                            {teamForm.image_url && (
                                                <div className="flex items-center gap-2">
                                                    <img src={teamForm.image_url} alt="Preview" className="h-10 w-10 object-cover rounded-full border border-gray-300" />
                                                    <button 
                                                        type="button"
                                                        onClick={() => setTeamForm(prev => ({ ...prev, image_url: '' }))}
                                                        className="bg-red-500/10 hover:bg-red-500/20 text-red-500 p-2 rounded transition-colors"
                                                        title="Remove Image"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <Button type="submit" className={`w-full justify-center mt-2 ${editingId ? 'bg-blue-600 hover:bg-blue-700 text-gray-900' : ''}`}>
                                        {editingId ? 'Update Member' : 'Add Member'}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    )}


                    {activeTab === 'projects' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* List */}
                            <div className="lg:col-span-2 space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Work / Projects</h2>
                                {loading ? (
                                    <div className="text-gray-500 py-10 text-center">Loading projects...</div>
                                ) : projects.length > 0 ? (
                                    projects.map(project => (
                                        <div key={project.id} className="bg-gray-100 p-6 rounded-xl border border-gray-200 flex justify-between items-center group">
                                            <div className="flex items-center gap-4">
                                                {project.image_url ? (
                                                    <img src={project.image_url} alt={project.title} className="w-24 h-16 object-cover rounded-md border border-gray-200" />
                                                ) : (
                                                    <div className="w-24 h-16 rounded-md bg-gray-200 flex items-center justify-center text-gray-500 text-xs">
                                                        No Image
                                                    </div>
                                                )}
                                                <div>
                                                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#bebd19] transition-colors">{project.title}</h3>
                                                    <p className="text-sm text-gray-400">{project.category}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleEditProject(project)} className="text-blue-500 hover:bg-blue-500/10 p-2 rounded-lg transition-colors">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button onClick={() => handleDeleteProject(project.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-10 bg-gray-100 rounded-xl border border-gray-200 border-dashed">
                                        <p className="text-gray-400">No projects added yet.</p>
                                    </div>
                                )}
                            </div>

                            {/* Add/Edit Form */}
                            <div className="bg-gray-100 p-6 rounded-xl border border-gray-200 h-fit sticky top-24">
                                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                                        {editingId ? 'Edit Project' : 'Add New Project'}
                                    </span>
                                    {editingId && (
                                        <button onClick={resetForms} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
                                            <X size={14} /> Cancel
                                        </button>
                                    )}
                                </h2>
                                <form onSubmit={handleSubmitProject} className="space-y-3">
                                    <input required placeholder="Project Title" value={projectForm.title} onChange={e => setProjectForm({ ...projectForm, title: e.target.value })} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />
                                    <input required placeholder="Category (e.g. Web App, Mobile App)" value={projectForm.category} onChange={e => setProjectForm({ ...projectForm, category: e.target.value })} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />
                                    
                                    {/* Image Upload */}
                                    <div className="space-y-2">
                                        <label className="block text-gray-500 text-sm">Project Image</label>
                                        <div className="flex gap-2 items-center">
                                            <label className="cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-900 px-3 py-2 rounded flex items-center gap-2 text-sm transition-colors">
                                                <Upload size={16} />
                                                {uploading ? 'Uploading...' : 'Choose Image'}
                                                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'project')} className="hidden" disabled={uploading} />
                                            </label>
                                            
                                            {projectForm.image_url && (
                                                <div className="flex items-center gap-2">
                                                    <img src={projectForm.image_url} alt="Preview" className="h-10 w-10 object-cover rounded border border-gray-300" />
                                                    <button 
                                                        type="button"
                                                        onClick={() => setProjectForm(prev => ({ ...prev, image_url: '' }))}
                                                        className="bg-red-500/10 hover:bg-red-500/20 text-red-500 p-2 rounded transition-colors"
                                                        title="Remove Image"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <textarea required rows="4" placeholder="Description" value={projectForm.description} onChange={e => setProjectForm({ ...projectForm, description: e.target.value })} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />
                                    <input placeholder="Project Link (Optional)" value={projectForm.project_link} onChange={e => setProjectForm({ ...projectForm, project_link: e.target.value })} className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none" />

                                    <Button type="submit" className={`w-full justify-center mt-2 ${editingId ? 'bg-blue-600 hover:bg-blue-700 text-gray-900' : ''}`}>
                                        {editingId ? 'Update Project' : 'Add Project'}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    )}

                    {activeTab === 'blogs' && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Blog Posts List */}
                            <div className="lg:col-span-2 space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Published & Draft Articles</h2>
                                {loading ? (
                                    <div className="text-gray-500 py-10 text-center">Loading articles...</div>
                                ) : blogs.length > 0 ? (
                                    blogs.map(blog => (
                                        <div key={blog.id} className="bg-gray-100 p-6 rounded-xl border border-gray-200 flex justify-between items-center group">
                                            <div className="flex items-center gap-4">
                                                {blog.cover_image ? (
                                                    <img src={blog.cover_image} alt={blog.title} className="w-20 h-16 object-cover rounded-md border border-gray-200" />
                                                ) : (
                                                    <div className="w-20 h-16 rounded-md bg-gray-200 flex items-center justify-center text-gray-400">
                                                        <BookOpen size={20} />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs font-bold px-2 py-0.5 rounded bg-[#bebd19]/20 text-gray-900">{blog.category || 'General'}</span>
                                                        {blog.is_published !== false ? (
                                                            <span className="text-xs font-semibold text-green-600 flex items-center gap-1">
                                                                <CheckCircle size={12} /> Published
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs font-semibold text-amber-600 flex items-center gap-1">
                                                                <XCircle size={12} /> Draft
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#bebd19] transition-colors">{blog.title}</h3>
                                                    <p className="text-xs text-gray-400">/blog/{blog.slug} • By {blog.author || 'PyrusMedia'}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <a 
                                                    href={`/blog/${blog.slug}`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="text-gray-500 hover:bg-gray-200 p-2 rounded-lg transition-colors"
                                                    title="Preview Article"
                                                >
                                                    <Eye size={18} />
                                                </a>
                                                <button onClick={() => handleEditBlog(blog)} className="text-blue-500 hover:bg-blue-500/10 p-2 rounded-lg transition-colors" title="Edit Article">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button onClick={() => handleDeleteBlog(blog.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-colors" title="Delete Article">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-12 bg-gray-100 rounded-xl border border-gray-200 border-dashed">
                                        <BookOpen className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                        <p className="text-gray-500 font-medium">No blog articles published yet.</p>
                                        <p className="text-xs text-gray-400 mt-1">Use the form on the right to publish your first article.</p>
                                    </div>
                                )}
                            </div>

                            {/* Add/Edit Blog Article Form */}
                            <div className="bg-gray-100 p-6 rounded-xl border border-gray-200 h-fit sticky top-24">
                                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                                        {editingId ? 'Edit Article' : 'Write New Article'}
                                    </span>
                                    {editingId && (
                                        <button onClick={resetForms} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
                                            <X size={14} /> Cancel
                                        </button>
                                    )}
                                </h2>
                                <form onSubmit={handleSubmitBlog} className="space-y-3">
                                    <div>
                                        <label className="block text-gray-600 text-xs mb-1 font-semibold">Article Title</label>
                                        <input 
                                            required 
                                            placeholder="e.g. How to Build Mobile Apps with AI" 
                                            value={blogForm.title} 
                                            onChange={e => {
                                                const titleVal = e.target.value;
                                                setBlogForm(prev => ({
                                                    ...prev,
                                                    title: titleVal,
                                                    slug: editingId ? prev.slug : createSlug(titleVal)
                                                }));
                                            }} 
                                            className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none text-sm" 
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-gray-600 text-xs mb-1 font-semibold">URL Slug</label>
                                        <input 
                                            required 
                                            placeholder="how-to-build-mobile-apps-with-ai" 
                                            value={blogForm.slug} 
                                            onChange={e => setBlogForm(prev => ({ ...prev, slug: createSlug(e.target.value) }))} 
                                            className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none text-xs font-mono" 
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-gray-600 text-xs mb-1 font-semibold">Category</label>
                                            <input 
                                                required 
                                                placeholder="e.g. UI/UX, AI, Dev" 
                                                value={blogForm.category} 
                                                onChange={e => setBlogForm(prev => ({ ...prev, category: e.target.value }))} 
                                                className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none text-xs" 
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-gray-600 text-xs mb-1 font-semibold">Read Time</label>
                                            <input 
                                                placeholder="e.g. 5 min read" 
                                                value={blogForm.read_time} 
                                                onChange={e => setBlogForm(prev => ({ ...prev, read_time: e.target.value }))} 
                                                className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none text-xs" 
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-gray-600 text-xs mb-1 font-semibold">Author</label>
                                        <input 
                                            placeholder="PyrusMedia Team" 
                                            value={blogForm.author} 
                                            onChange={e => setBlogForm(prev => ({ ...prev, author: e.target.value }))} 
                                            className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none text-xs" 
                                        />
                                    </div>

                                    {/* Cover Image Upload */}
                                    <div className="space-y-2">
                                        <label className="block text-gray-600 text-xs font-semibold">Cover Image</label>
                                        <div className="flex gap-2 items-center">
                                            <label className="cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-900 px-3 py-2 rounded flex items-center gap-2 text-xs transition-colors">
                                                <Upload size={14} />
                                                {uploading ? 'Uploading...' : 'Choose Image'}
                                                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'blog')} className="hidden" disabled={uploading} />
                                            </label>
                                            
                                            {blogForm.cover_image && (
                                                <div className="flex items-center gap-2">
                                                    <img src={blogForm.cover_image} alt="Preview" className="h-9 w-12 object-cover rounded border border-gray-300" />
                                                    <button 
                                                        type="button"
                                                        onClick={() => setBlogForm(prev => ({ ...prev, cover_image: '' }))}
                                                        className="bg-red-500/10 hover:bg-red-500/20 text-red-500 p-1.5 rounded transition-colors"
                                                        title="Remove Image"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-gray-600 text-xs mb-1 font-semibold">Excerpt (Summary)</label>
                                        <textarea 
                                            rows="2" 
                                            placeholder="Short 1-2 sentence preview..." 
                                            value={blogForm.excerpt} 
                                            onChange={e => setBlogForm(prev => ({ ...prev, excerpt: e.target.value }))} 
                                            className="w-full bg-white border border-gray-300 rounded p-2 text-gray-900 focus:border-[#bebd19] outline-none text-xs" 
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-gray-600 text-xs mb-1 font-semibold">Full Article Content</label>
                                        <div className="bg-white text-black rounded">
                                            <ReactQuill
                                                theme="snow"
                                                value={blogForm.content}
                                                onChange={value => setBlogForm(prev => ({ ...prev, content: value }))}
                                                placeholder="Write your article content here..."
                                                className="bg-white text-black rounded"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 pt-2">
                                        <input 
                                            type="checkbox"
                                            id="is_published"
                                            checked={blogForm.is_published}
                                            onChange={e => setBlogForm(prev => ({ ...prev, is_published: e.target.checked }))}
                                            className="w-4 h-4 text-[#bebd19] rounded focus:ring-0"
                                        />
                                        <label htmlFor="is_published" className="text-xs font-semibold text-gray-800 cursor-pointer">
                                            Publish Article Immediately
                                        </label>
                                    </div>

                                    <Button type="submit" disabled={uploading} className={`w-full justify-center mt-2 ${editingId ? 'bg-blue-600 hover:bg-blue-700 text-gray-900' : ''}`}>
                                        {uploading ? 'Uploading...' : (editingId ? 'Update Article' : 'Publish Article')}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <CustomDialog
                isOpen={dialog.isOpen}
                title={dialog.title}
                message={dialog.message}
                type={dialog.type}
                onClose={closeDialog}
                onConfirm={handleConfirmAction}
                confirmText="Delete"
            />
        </>
    );
};

export default AdminDashboard;
